/**
 * Attaches the current UI locale to every API request as `X-App-Language`
 * so backend LLM services can localize AI-generated replies.
 *
 * Patches window.fetch and XMLHttpRequest once at app bootstrap. Covers both
 * raw fetch() calls and all axios traffic (axios uses XHR in the browser)
 * without having to touch every call site. Only requests to our own API
 * hosts get the header — third-party endpoints (Stripe, OAuth providers,
 * etc.) are untouched so their CORS contracts are unaffected.
 */
import i18n from 'i18next';
import { getApiBaseUrl } from '../utils/apiBase';
import { resolveApiRequestUrl } from '../utils/apiOriginPolicy';

const RELEASE_CHANNEL = process.env.REACT_APP_RELEASE_CHANNEL || '';

function resolveRequestUrl(rawUrl) {
  return resolveApiRequestUrl({
    url: rawUrl,
    configuredBase: /api\.xpherium\.com/i.test(String(rawUrl || '')) ? getApiBaseUrl() : '',
    channel: RELEASE_CHANNEL,
  });
}

const HEADER = 'X-App-Language';

function currentLanguage() {
  return (i18n.language || 'en').split('-')[0];
}

function isApiUrl(rawUrl) {
  try {
    const u = new URL(rawUrl, window.location.origin);
    if (!/^\/api(\/|$)/.test(u.pathname)) return false;
    return (
      /(^|\.)xpherium\.com$/i.test(u.hostname) ||
      u.hostname === 'localhost' ||
      u.hostname === '127.0.0.1' ||
      u.origin === window.location.origin
    );
  } catch {
    return false;
  }
}

export function installApiLanguageHeader() {
  if (typeof window === 'undefined' || window.__xphLangHeaderInstalled) return;
  window.__xphLangHeaderInstalled = true;

  // ── fetch ──
  const origFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const rawUrl = typeof input === 'string' ? input : input?.url || '';
    const url = resolveRequestUrl(rawUrl);
    if (typeof input === 'string') {
      input = url;
    } else if (url !== rawUrl && input) {
      input = new Request(url, input);
    }
    try {
      if (isApiUrl(url)) {
        const headers = new Headers(
          (init && init.headers) || (typeof input !== 'string' ? input?.headers : undefined) || {}
        );
        if (!headers.has(HEADER)) headers.set(HEADER, currentLanguage());
        init = { ...(init || {}), headers };
      }
    } catch {
      /* never block a request over the locale header */
    }
    return origFetch(input, init);
  };

  // ── XMLHttpRequest (covers axios) ──
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (...args) {
    if (args.length > 1 && args[1]) args[1] = resolveRequestUrl(args[1]);
    this.__xphIsApi = isApiUrl(args[1]);
    return origOpen.apply(this, args);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (this.__xphIsApi) {
      try {
        this.setRequestHeader(HEADER, currentLanguage());
      } catch {
        /* header already set or request in invalid state — ignore */
      }
    }
    return origSend.apply(this, args);
  };
}
